import React from 'react';
import moment from 'moment';
import Moment from 'react-moment';
import axios from 'axios';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { withTranslation } from 'react-i18next';
import { StickyTable, Row, Cell } from 'react-sticky-table';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import { setData } from '../../actions/index';
import CalendarHeader from '../parts/CalendarHeader';
import { capitalizeFirstLetter } from '../../helpers';

const styles = theme => ({
  sectionHeading: {
    color: '#394f5a',
  },
  tableWrapper: {
    overflow: 'auto',
  },
  table: {
    borderSpacing: 0,
    width: '100%',
    maxWidth: '100%',
    '& thead tr th': {
      color: '#666',
      fontSize: 14,
      borderBottom: '1px solid #000',
      fontWeight: 'normal',
      padding: '5px 10px',
      textAlign: 'center',
    },
    '& tbody tr td': {
      borderBottom: '1px solid #ddd',
      borderRight: '1px solid #ddd',
    },
  },
  dateRow: {
    padding: 4,
    minWidth: 40,
    fontSize: 14,
    lineHeight: 1.4,
    color: '#666',
    backgroundColor: '#fff',
    whiteSpace: 'nowrap',
    textAlign: 'center',
  },
  sessionRow: {
    padding: 4,
    minWidth: 16,
    fontSize: 14,
    lineHeight: 1.4,
    color: '#666',
    backgroundColor: '#fff',
    whiteSpace: 'nowrap',
    textAlign: 'center',
  },
  attendanceRow: {
    padding: 4,
    height: 32,
    width: 48,
    textAlign: 'center',
    border: '1px solid #ddd',
  },
  attendanceRowYes: {
    backgroundColor: '#8AC44B',
  },
  attendanceRowPresent: {
    backgroundColor: '#8AC44B',
  },
  attendanceRowNo: {
    backgroundColor: '#BB2D2D',
  },
  attendanceRowAbsent: {
    backgroundColor: '#BB2D2D',
  },
  attendanceRowExcused: {
    backgroundColor: '#5bc0de',
  },
  attendanceRowLate: {
    backgroundColor: '#F0C920',
  },
  attendancePercentage: {
    fontWeight: 'normal',
  },
  legend: {
    marginTop: 40,
  },
  legendItem: {
    paddingRight: 30,
  },
  legendItemBox: {
    display: 'inline-block',
    width: 48,
    height: 32,
  },
  legendItemText: {
    display: 'inline-block',
    verticalAlign: 'top',
    paddingLeft: 10,
    lineHeight: '32px',
  },
});

const mapStateToProps = state => {
  return {
    selectedPupil: state.selectedPupil,
    defaultDateRange: state.defaultDateRange,
  };
};

function mapDispatchToProps(dispatch) {
  return {
    setData: (data, type) => dispatch(setData(data, type)),
  };
}

class AttendancePage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      dateStart: null,
      dateEnd: null,
      isLoading: false,
      isLoaded: false,
      data: [],
      meta: {},
    };

    this.axiosCancel = null;

    this.onRangeChanged = this.onRangeChanged.bind(this);
  }

  componentDidMount() {
    const {
      defaultDateRange,
    } = this.props;

    if (defaultDateRange !== null) {
      this.setState({
        dateStart: defaultDateRange[0],
        dateEnd: defaultDateRange[1],
      }, () => {
        this.reloadData();
      });
    } else {
      this.reloadData();
    }
  }

  componentWillUnmount() {
    if (this.axiosCancel) {
      this.axiosCancel();
    }
  }

  reloadData() {
    const {
      selectedPupil,
    } = this.props;
    const {
      dateStart,
      dateEnd,
    } = this.state;

    if (!selectedPupil) {
      return;
    }

    this.setState({
      isLoading: true,
    });

    let dateQuery = '';
    if (dateStart && dateEnd) {
      dateQuery = `?from=${moment(dateStart).format('YYYY-MM-DD')}&to=${moment(dateEnd).format('YYYY-MM-DD')}`;
    }

    axios
      .get(
        `${window.apiUrl}/attendance/${selectedPupil.id}${dateQuery}`,
        {
          cancelToken: new axios.CancelToken((c) => { this.axiosCancel = c }),
        }
      )
      .then((response) => {
        if (response.data.success) {
          this.setState({
            meta: response.data.meta,
            data: response.data.data,
            dateStart: response.data.meta.start_date,
            dateEnd: response.data.meta.end_date,
          });
        }

        this.setState({
          isLoading: false,
          isLoaded: true,
        });
      })
      .catch((thrown) => {});
  }

  onRangeChanged(range) {
    const dateStart = range[0].format('YYYY-MM-DD');
    const dateEnd = range[1].format('YYYY-MM-DD');

    this.setState({
      dateStart,
      dateEnd,
    }, () => {
      this.reloadData();
    });

    this.props.setData(range, 'defaultDateRange');
  }

  render() {
    const {
      classes,
      t,
      selectedPupil,
    } = this.props;
    const {
      dateStart,
      dateEnd,
      data,
      meta,
    } = this.state;

    const hasData = meta && meta.dates && meta.dates.length > 0;
    const datesVertical = isMobileApp;

    return (
      <div className="attendance-page">
        <CalendarHeader
          dateText={`${t('Date')}:`}
          dateFormat={dateFormat}
          dateStart={dateStart}
          dateEnd={dateEnd}
          onRangeChanged={this.onRangeChanged}
          useSuspense={false}
        />
        {!hasData && (
          <p>{t('No activity found for the selected date range.')}</p>
        )}
        {hasData && (
          <div>
            {selectedPupil.display_attendance_percentage && (
              <>
                <h3 className={classes.attendancePercentage}>
                  <div>
                    {t('Attendance percentage')}
                    <span>:&nbsp;</span>
                  </div>
                </h3>
                <ul>
                  <li>
                    {t('Selected date range')}
                    <span>:&nbsp;</span>
                    {meta.percentage}
                    <span>%</span>
                  </li>
                  <li>
                    {t('Since August')}
                    <span>:&nbsp;</span>
                    {meta.percentage_singe_august}
                    <span>%</span>
                  </li>
                </ul>
              </>
            )}
            <div className={classes.tableWrapper}>
              {datesVertical
                && (
                <table className={classes.table}>
                  <thead>
                    <tr>
                      <th className={classes.dateRow} />
                      {meta.sessions.map(session => (
                        <th key={session}>
                          {session.replace('Period ', 'P')}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {meta.dates.map(date => (
                      <tr key={date}>
                        <td className={classes.dateRow}>
                          <Moment date={date} format="DD" />
                          <br />
                          <Moment date={date} format="MMM" />
                        </td>
                        {meta.sessions.map((session) => {
                          const key = `${date}_${session}`;
                          const entry = data[date][session];
                          if (!entry) {
                            return (
                              <td
                                key={key}
                                className={classes.attendanceRow}
                              />
                            );
                          }
                          const className = `
                            ${classes.attendanceRow}
                            ${classes[`attendanceRow${capitalizeFirstLetter(entry.status)}`]}
                          `;
                          return (
                            <td
                              key={key}
                              className={className}
                            />
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {!datesVertical && (
                <StickyTable stickyHeaderCount={0} stickyColumnCount={1}>
                  <Row>
                    <Cell className={classes.sessionRow} />
                    {meta.dates.map(date => (
                      <Cell key={date} className={classes.dateRow}>
                        <Moment date={date} format="ddd" />
                        <br />
                        <Moment date={date} format="DD" />
                        <br />
                        <Moment date={date} format="MMM" />
                      </Cell>
                    ))}
                  </Row>
                  {meta.sessions.map(session => (
                    <Row key={session}>
                      <Cell className={classes.sessionRow}>
                        {session}
                      </Cell>
                      {Object.keys(data).map((date) => {
                        const key = `${date}_${session}`;
                        const entry = data[date][session];
                        if (!entry) {
                          return (
                            <Cell
                              key={key}
                              className={classes.attendanceRow}
                            />
                          );
                        }
                        const className = `
                          ${classes.attendanceRow}
                          ${classes[`attendanceRow${capitalizeFirstLetter(entry.status)}`]}
                        `;
                        return (
                          <Cell
                            key={key}
                            className={className}
                          />
                        );
                      })}
                    </Row>
                  ))}
                </StickyTable>
              )}
            </div>
            <Grid container className={classes.legend}>
              <Grid item xs={12} sm={12} md="auto" className={classes.legendItem}>
                <div className={`${classes.legendItemBox} ${classes.attendanceRowPresent}`} />
                <div className={classes.legendItemText}>{t('Present')}</div>
              </Grid>
              <Grid item xs={12} sm={12} md="auto" className={classes.legendItem}>
                <div className={`${classes.legendItemBox} ${classes.attendanceRowExcused}`} />
                <div className={classes.legendItemText}>{t('Authorised absence')}</div>
              </Grid>
              <Grid item xs={12} sm={12} md="auto" className={classes.legendItem}>
                <div className={`${classes.legendItemBox} ${classes.attendanceRowLate}`} />
                <div className={classes.legendItemText}>{t('Late')}</div>
              </Grid>
              <Grid item xs={12} sm={12} md="auto" className={classes.legendItem}>
                <div className={`${classes.legendItemBox} ${classes.attendanceRowAbsent}`} />
                <div className={classes.legendItemText}>{t('Absent')}</div>
              </Grid>
            </Grid>
          </div>
        )}
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(styles)(withTranslation()(AttendancePage))
);
